/**
 * The resource declarations a page's `hello` may carry, and the one matcher that routes a `read` to
 * the handler that serves it (PRD `agentic-app-direct-bridge` FR-8).
 *
 * The same grammar as the host (`platform/crates/agentic-app-host/src/bridge/resource_template.rs`):
 * an exact URI, or literal text with `{name}` variables, where a variable matches one or more
 * characters other than `/`, `?` and `#`. No operators, no explode, no two variables side by side,
 * and the scheme is always literal. A page that declared something outside it would be refused at
 * `hello`, so the library refuses it at registration instead.
 */

type Part = { readonly literal: string } | { readonly variable: true };

function parse(declaration: string): Part[] | null {
  const parts: Part[] = [];
  let rest = declaration;
  while (rest.length > 0) {
    const at = rest.search(/[{}]/);
    if (at === 0) {
      if (rest[0] === "}") return null;
      const close = rest.indexOf("}");
      if (close < 0) return null;
      const name = rest.slice(1, close);
      if (!/^[A-Za-z_][A-Za-z0-9_]*$/.test(name)) return null;
      const last = parts.at(-1);
      if (last && "variable" in last) return null;
      parts.push({ variable: true });
      rest = rest.slice(close + 1);
    } else if (at > 0) {
      if (rest[at] === "}") return null;
      parts.push({ literal: rest.slice(0, at) });
      rest = rest.slice(at);
    } else {
      parts.push({ literal: rest });
      rest = "";
    }
  }
  const first = parts[0];
  return first && "literal" in first && first.literal.includes(":") ? parts : null;
}

function matchesFrom(parts: readonly Part[], uri: string): boolean {
  const [head, ...rest] = parts;
  if (!head) return uri.length === 0;
  if ("literal" in head) return uri.startsWith(head.literal) && matchesFrom(rest, uri.slice(head.literal.length));
  const delimiter = uri.search(/[/?#]/);
  const segment = delimiter < 0 ? uri.length : delimiter;
  for (let taken = 1; taken <= segment; taken += 1) {
    if (matchesFrom(rest, uri.slice(taken))) return true;
  }
  return false;
}

/** Whether `declaration` is an exact URI or a template in the grammar. */
export function declarationIsValid(declaration: string): boolean {
  return parse(declaration) !== null;
}

/** Whether `uri` is the URI `declaration` names, or one its template matches. Invalid matches nothing. */
export function declarationMatches(declaration: string, uri: string): boolean {
  const parts = parse(declaration);
  return parts !== null && matchesFrom(parts, uri);
}

/**
 * The values `uri` binds to `declaration`'s variables, by name (shortest match first, as the matcher
 * takes them), or `null` when it does not match.
 */
export function templateVariables(declaration: string, uri: string): Record<string, string> | null {
  const names = [...declaration.matchAll(/\{([A-Za-z_][A-Za-z0-9_]*)\}/g)].map((m) => m[1]!);
  const parts = parse(declaration);
  if (!parts) return null;
  const values: string[] = [];
  const walk = (index: number, tail: string): boolean => {
    const head = parts[index];
    if (!head) return tail.length === 0;
    if ("literal" in head) return tail.startsWith(head.literal) && walk(index + 1, tail.slice(head.literal.length));
    const delimiter = tail.search(/[/?#]/);
    const segment = delimiter < 0 ? tail.length : delimiter;
    for (let taken = 1; taken <= segment; taken += 1) {
      values.push(tail.slice(0, taken));
      if (walk(index + 1, tail.slice(taken))) return true;
      values.pop();
    }
    return false;
  };
  if (!walk(0, uri)) return null;
  return Object.fromEntries(names.map((name, i) => [name, values[i]!]));
}
